import React, { createContext, useEffect, useState } from "react";

// The context now delivers **undefined** while loading, an **array** on success,
// and **null** on failure so consuming components can distinguish states.
export const SubstackFeedContext = createContext(undefined);

// URL of the Substack feed we want to consume
const SUBSTACK_BASE_URL = process.env.REACT_APP_WRITING_URL;
const SUBSTACK_FEED_URL = `${SUBSTACK_BASE_URL}feed`;
// Cloudflare-worker proxy (configurable via .env). Ex:
// REACT_APP_RSS_PROXY="https://geometry-rss-proxy.ben-ee2.workers.dev"
const RSS_PROXY_BASE = process.env.REACT_APP_RSS_PROXY || "https://geometry-rss-proxy.ben-ee2.workers.dev";

export function SubstackFeedProvider({ children }) {
  // undefined = loading, array = success, null = failure
  const [posts, setPosts] = useState(undefined);

  useEffect(() => {
    let isMounted = true;

    // Fetch the RSS feed only once when the provider mounts
    const fetchFeed = async () => {
      // Convert either JSON (from Cloudflare worker) or XML (direct RSS) into array of posts
      const parseFeed = (rawText) => {
        // Attempt JSON first (worker returns JSON)
        try {
          const json = JSON.parse(rawText);
          // 1) Simple rss-parser output (items [])
          if (Array.isArray(json.items)) {
            return json.items.map((item) => ({
              title: item.title ?? "",
              link: item.link ?? "",
              description: item.contentSnippet ?? item.content ?? item.description ?? "",
              pubDate: new Date(item.pubDate ?? item.isoDate ?? 0),
            }));
          }

          // 2) xml2js-style: json.rss.channel.item (object or array)
          const channel = json.rss?.channel;
          if (channel) {
            let items = channel.item;
            if (items) {
              if (!Array.isArray(items)) items = [items]; // ensure array
              return items.map((item) => ({
                title: item.title ?? item["#text"] ?? "",
                link: item.link ?? item.guid?.["#text"] ?? "",
                description: item.description ?? item["content:encoded"] ?? "",
                pubDate: new Date(item.pubDate ?? 0),
              }));
            }
          }
        } catch (_) {
          /* Not JSON or unrecognised structure – proceed to XML */
        }

        // Fallback: parse as RSS XML
        const parsed = new window.DOMParser().parseFromString(rawText, "text/xml");
        const items = parsed.querySelectorAll("item");
        return Array.from(items).map((item) => ({
          title: item.querySelector("title")?.textContent ?? "",
          link: item.querySelector("link")?.textContent ?? "",
          description: item.querySelector("description")?.textContent ?? "",
          pubDate: new Date(item.querySelector("pubDate")?.textContent ?? 0),
        }));
      };

      try {
        // Fetch through Cloudflare worker (handles CORS for us)
        const proxyResp = await fetch(`${RSS_PROXY_BASE}?url=${encodeURIComponent(SUBSTACK_FEED_URL)}`);
        if (!proxyResp.ok) throw new Error(`Proxy returned ${proxyResp.status}`);
        const rawText = await proxyResp.text();
        if (isMounted) setPosts(parseFeed(rawText));
      } catch (proxyErr) {
        // Fallback: try direct fetch (Safari/iOS sometimes blocks proxy requests)
        console.warn("Substack feed proxy failed, attempting direct fetch", proxyErr);
        try {
          const directResp = await fetch(SUBSTACK_FEED_URL);
          if (!directResp.ok) throw new Error(`Direct fetch returned ${directResp.status}`);
          const rawText = await directResp.text();
          if (isMounted) setPosts(parseFeed(rawText));
        } catch (directErr) {
          console.error("Error fetching Substack feed (both proxy & direct):", directErr);
          if (isMounted) setPosts(null); // signal failure
        }
      }
    };

    fetchFeed();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <SubstackFeedContext.Provider value={posts}>{children}</SubstackFeedContext.Provider>
  );
} 